import PropTypes from 'prop-types';

import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

import ImmutablePropTypes from 'react-immutable-proptypes';
import ImmutablePureComponent from 'react-immutable-pure-component';

import { HotKeys } from 'react-hotkeys';


import api from 'mastodon/api';
import { ContentWarning } from 'mastodon/components/content_warning';
import { FilterWarning } from 'mastodon/components/filter_warning';
import { FollowButton } from 'mastodon/components/follow_button';
import { Icon } from 'mastodon/components/icon';
import PictureInPicturePlaceholder from 'mastodon/components/picture_in_picture_placeholder';
import { withOptionalRouter, WithOptionalRouterPropTypes } from 'mastodon/utils/react_router';
import { me } from 'mastodon/initial_state';

import Card from '../features/status/components/card';
import Bundle from '../features/ui/components/bundle';
import { MediaGallery, Video, Audio } from '../features/ui/util/async-components';
import { SensitiveMediaContext } from '../features/ui/util/sensitive_media_context';
import { displayMedia } from '../initial_state';

import { Avatar } from './avatar';
import { DisplayName } from './display_name';
import { getHashtagBarForStatus } from './hashtag_bar';
import { RelativeTimestamp } from './relative_timestamp';
import StatusActionBar from './status_action_bar';
import StatusContent from './status_content';
import { StatusThreadLabel } from './status_thread_label';
import { VisibilityIcon } from './visibility_icon';
import { PushPinIcon } from './icons/push_pin';
import { MentionIcon } from './icons/mention';

const domParser = new DOMParser();

export const textForScreenReader = (intl, status, rebloggedByText = false) => {
  const displayName = status.getIn(['account', 'display_name']);
  const spoilerText = status.getIn(['translation', 'spoiler_text']) || status.get('spoiler_text');
  const contentHtml = status.getIn(['translation', 'contentHtml']) || status.get('contentHtml');
  const contentText = domParser.parseFromString(contentHtml, 'text/html').documentElement.textContent;

  const values = [
    displayName.length === 0 ? status.getIn(['account', 'acct']).split('@')[0] : displayName,
    spoilerText && status.get('hidden') ? spoilerText : contentText,
    intl.formatDate(status.get('created_at'), { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' }),
    status.getIn(['account', 'acct']),
  ];

  if (rebloggedByText) {
    values.push(rebloggedByText);
  }

  return values.join(', ');
};

export const defaultMediaVisibility = (status) => {
  if (!status) {
    return undefined;
  }

  if (status.get('reblog', null) !== null && typeof status.get('reblog') === 'object') {
    status = status.get('reblog');
  }

  return (displayMedia !== 'hide_all' && !status.get('sensitive') || displayMedia === 'show_all');
};

const messages = defineMessages({
  public_short: { id: 'privacy.public.short', defaultMessage: 'Public' },
  unlisted_short: { id: 'privacy.unlisted.short', defaultMessage: 'Quiet public' },
  private_short: { id: 'privacy.private.short', defaultMessage: 'Followers' },
  direct_short: { id: 'privacy.direct.short', defaultMessage: 'Specific people' },
  edited: { id: 'status.edited', defaultMessage: 'Edited {date}' },
});

class Status extends ImmutablePureComponent {
  static contextType = SensitiveMediaContext;

  static propTypes = {
    contextType: PropTypes.string,
    isNotification: PropTypes.bool,
    childRepliesSize: PropTypes.number,
    expandReply: PropTypes.func,
    isReplyExpand: PropTypes.bool,
    isComment: PropTypes.bool,
    status: ImmutablePropTypes.map,
    account: ImmutablePropTypes.record,
    previousId: PropTypes.string,
    nextInReplyToId: PropTypes.string,
    rootId: PropTypes.string,
    onClick: PropTypes.func,
    onReply: PropTypes.func,
    onFavourite: PropTypes.func,
    onReblog: PropTypes.func,
    onDelete: PropTypes.func,
    onDirect: PropTypes.func,
    onMention: PropTypes.func,
    onPin: PropTypes.func,
    onOpenMedia: PropTypes.func,
    onOpenVideo: PropTypes.func,
    onBlock: PropTypes.func,
    onAddFilter: PropTypes.func,
    onEmbed: PropTypes.func,
    onHeightChange: PropTypes.func,
    onToggleHidden: PropTypes.func,
    onToggleCollapsed: PropTypes.func,
    onTranslate: PropTypes.func,
    onInteractionModal: PropTypes.func,
    muted: PropTypes.bool,
    hidden: PropTypes.bool,
    unread: PropTypes.bool,
    onMoveUp: PropTypes.func,
    onMoveDown: PropTypes.func,
    showThread: PropTypes.bool,
    isImageModal: PropTypes.bool,
    isVideoModal: PropTypes.bool,
    getScrollPosition: PropTypes.func,
    updateScrollBottom: PropTypes.func,
    cacheMediaWidth: PropTypes.func,
    cachedMediaWidth: PropTypes.number,
    scrollKey: PropTypes.string,
    skipPrepend: PropTypes.bool,
    avatarSize: PropTypes.number,
    deployPictureInPicture: PropTypes.func,
    unfocusable: PropTypes.bool,
    pictureInPicture: ImmutablePropTypes.contains({
      inUse: PropTypes.bool,
      available: PropTypes.bool,
    }),
    onToggleReplies: PropTypes.func,
    onShowCommentForm: PropTypes.func,
    shareDetailCompose: PropTypes.func.isRequired,
    ...WithOptionalRouterPropTypes,
  };

  updateOnProps = [
    'status',
    'account',
    'muted',
    'hidden',
    'unread',
    'pictureInPicture',
    'commentId',
    'isReplyExpand',
    'commentId'
  ];

  state = {
    showMedia: defaultMediaVisibility(this.props.status) && !(this.context?.hideMediaByDefault),
    showDespiteFilter: undefined,
    isHighlighted: false,
    hasBeenClicked: false,
    highlightCommentId: null,
    isLikeComment: false,
    showSensitiveContent: this._properStatus() && this._properStatus().get ? !this._properStatus().get('sensitive') : false,
  };

  componentDidMount() {
    const { status, location, shareDetailCompose } = this.props;

    if (status && this.hasThreePathSegments(location?.pathname || '')) {
      shareDetailCompose(status);
    }

    const searchParams = new URLSearchParams(location?.search || '');
    const highlightCommentId = searchParams.get('highlightCommentId');
    const isLikeComment = !!searchParams.get('likeComment');

    if (highlightCommentId) {

      this.setState({ highlightCommentId }, () => {
        this.updateHighlightStatus();
      });
    }

    if (isLikeComment) {
      this.setState({ isLikeComment: true });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location?.search !== prevProps.location?.search) {
      const searchParams = new URLSearchParams(this.props.location?.search || '');
      const highlightCommentId = searchParams.get('highlightCommentId');
      const isLikeComment = !!searchParams.get('likeComment');

      if (highlightCommentId !== this.state.highlightCommentId) {
        this.setState({ highlightCommentId, hasBeenClicked: false }, () => {
          this.updateHighlightStatus();
        });
      }

      if (isLikeComment) {
        this.setState({ isLikeComment: true });
      }
    }

    if (this.props.status?.get('id') !== prevProps.status?.get('id')) {
      this.setState({
        showMedia: defaultMediaVisibility(this.props.status) && !(this.context?.hideMediaByDefault),
        showDespiteFilter: undefined,
      });

      const isHighlighted = this.props.status?.get('id') === this.state.highlightCommentId;
      if (isHighlighted !== this.state.isHighlighted || this.state.hasBeenClicked) {
        this.setState({
          isHighlighted,
          hasBeenClicked: false,
        });
      }
      if (this.props.status && this.hasThreePathSegments(this.props.location?.pathname || '')) {
        this.props.shareDetailCompose(this.props.status);
      }
    }
  }

  updateHighlightStatus = () => {
    const isHighlighted = this.props.status?.get('id') === this.state.highlightCommentId;
    if (isHighlighted !== this.state.isHighlighted) {
      this.setState({ isHighlighted }, () => {
        if (isHighlighted && this.node) {
          this.node.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      });
    }
  };

  hasThreePathSegments = (pathname) => {
    const segments = pathname.split('/').filter(segment => segment.length > 0);
    return segments.length === 3;
  };

  handleMove = (e) => {
    e.preventDefault();
    if (!this.state.hasBeenClicked) {
      this.setState({ isHighlighted: false, hasBeenClicked: true });
    }
  };

  handleToggleMediaVisibility = () => {
    this.setState({ showMedia: !this.state.showMedia });
  };

  handleClick = e => {
    e.preventDefault();
    if (this.props.isComment) {
      return;
    }
    if (e?.button === 0 && !(e?.ctrlKey || e?.metaKey)) {
      this._openStatus();
    } else if (e?.button === 1 || (e?.button === 0 && (e?.ctrlKey || e?.metaKey))) {
      this._openStatus(true);
    }
  };

  handleMouseUp = e => {
    if (e.target !== e.currentTarget && e.detail >= 1) {
      return;
    }
    if (this.props.isComment) {
      return;
    }
    this.handleClick(e);
  };

  handleExpandedToggle = () => {
    this.props.onToggleHidden(this._properStatus());
  };

  handleCollapsedToggle = isCollapsed => {
    this.props.onToggleCollapsed(this._properStatus(), isCollapsed);
  };

  handleTranslate = () => {
    this.props.onTranslate(this._properStatus());
  };

  getAttachmentAspectRatio() {
    const attachments = this._properStatus().get('media_attachments');
    if (attachments.getIn([0, 'type']) === 'video') {
      return `${attachments.getIn([0, 'meta', 'original', 'width'])} / ${attachments.getIn([0, 'meta', 'original', 'height'])}`;
    } else if (attachments.getIn([0, 'type']) === 'audio') {
      return '16 / 9';
    } else {
      return (attachments.size === 1 && attachments.getIn([0, 'meta', 'small', 'aspect'])) ? attachments.getIn([0, 'meta', 'small', 'aspect']) : '3 / 2';
    }
  }

  renderLoadingMediaGallery = () => {
    return <div className='media-gallery' style={{ aspectRatio: this.getAttachmentAspectRatio() }} />;
  };

  renderLoadingVideoPlayer = () => {
    return <div className='video-player' style={{ aspectRatio: this.getAttachmentAspectRatio() }} />;
  };

  renderLoadingAudioPlayer = () => {
    return <div className='audio-player' style={{ aspectRatio: this.getAttachmentAspectRatio() }} />;
  };

  handleOpenVideo = (options) => {
    const lang = this._properStatus().getIn(['translation', 'language']) || this._properStatus().get('language');
    this.props.onOpenVideo(this._properStatus().get('id'), this._properStatus().getIn(['media_attachments', 0]), lang, options);
  };

  handleOpenMedia = (media, index) => {
    const lang = this._properStatus().getIn(['translation', 'language']) || this._properStatus().get('language');
    this.props.onOpenMedia(this._properStatus().get('id'), media, index, lang);
  };

  handleHotkeyOpenMedia = e => {
    const { onOpenMedia, onOpenVideo } = this.props;
    const status = this._properStatus();
    e.preventDefault();
    if (this._properStatus().get('media_attachments').size > 0) {
      const lang = this._properStatus().getIn(['translation', 'language']) || this._properStatus().get('language');
      if (status.getIn(['media_attachments', 0, 'type']) === 'video') {
        onOpenVideo(this._properStatus().get('id'), this._properStatus().getIn(['media_attachments', 0]), lang, { startTime: 0 });
      } else {
        onOpenMedia(this._properStatus().get('id'), this._properStatus().get('media_attachments'), 0, lang);
      }
    }
  };

  handleDeployPictureInPicture = (type, mediaProps) => {
    const { deployPictureInPicture } = this.props;
    deployPictureInPicture(this._properStatus(), type, mediaProps);
  };

  handleHotkeyReply = e => {
    const status = status;
    e.preventDefault();
    this.props.onReply(status);
  };

  handleHotkeyFavourite = () => {
    const status = status;
    this.props.onFavourite(status);
  };

  handleHotkeyBoost = e => {
    this.props.onReblog(status, e);
  };

  handleHotkeyMention = e => {
    e.preventDefault();
    this.props.onMention(status.get('account'));
  };

  handleHotkeyOpen = () => {
    this._openStatus();
  };

  handleToggleSensitiveContent = () => {
    this.setState((prevState) => ({
      showSensitiveContent: !prevState.showSensitiveContent,
    }));
  };

  _openStatus = (newTab = false) => {
    if (this.props.onClick) {
      this.props.onClick();
      return;
    }
    const { history } = this.props;
    const status = this._properStatus();
    if (!history) {
      return;
    }

    let path;
    const inReplyToId = status.get('in_reply_to_id');
    const mentionAccountId = status.get('in_reply_to_account_id');
    const currentId = inReplyToId ? inReplyToId : status.get('id');
    const highlightId = status.get('id');

    path = `/@${status.getIn(['account', 'acct'])}/${status.get('id')}`;

    if (newTab) {
      window.open(path, '_blank', 'noopener');
    } else {
      if (inReplyToId) {
        api()
          .get(`/api/v1/accounts/${mentionAccountId}`)
          .then((response) => {
            const username = response.data.username || status.get('acct');
            let path = `/@${username}/${currentId}?highlightCommentId=${highlightId}`;

            return api()
              .get(`/api/v1/statuses/${currentId}/context`)
              .then((contextResponse) => {
                const ancestors = contextResponse.data.ancestors || [];

                if (ancestors.length > 0) {
                  const ancestorIds = ancestors.map(ancestor => ancestor.id).join(',');
                  path = `/@${ancestors[0].account.acct}/${ancestors[0].id}?highlightCommentId=${highlightId}&ancestorIds=${ancestorIds}`;
                  window.location.replace(path);
                } else {
                  window.location.replace(path);
                }
              });
          })
          .catch((error) => {
            console.error('Failed to fetch account or context:', error);
            const path = `/@${status.getIn(['account', 'acct'])}/${currentId}?highlightCommentId=${highlightId}`;
            history.push(path);
          });
      } else {
        history.push(path);
      }
    }
  };

  handleHotkeyOpenProfile = () => {
    this._openProfile();
  };

  _openProfile = (proper = true) => {
    const { history } = this.props;
    const status = proper ? this._properStatus() : this.props.status;
    if (!history) {
      return;
    }
    history.push(`/@${status.getIn(['account', 'acct'])}`);
  };

  handleHotkeyMoveUp = e => {
    this.props.onMoveUp(this.props.status.get('id'), e.target.getAttribute('data-featured'));
  };

  handleHotkeyMoveDown = e => {
    this.props.onMoveDown(this.props.status.get('id'), e.target.getAttribute('data-featured'));
  };

  handleHotkeyToggleHidden = () => {
    const { onToggleHidden } = this.props;
    const status = this._properStatus();
    if (this.props.status.get('matched_filters')) {
      const expandedBecauseOfCW = !status.get('hidden') || status.get('spoiler_text').length === 0;
      const expandedBecauseOfFilter = this.state.showDespiteFilter;
      if (expandedBecauseOfFilter && !expandedBecauseOfCW) {
        onToggleHidden(status);
      } else if (expandedBecauseOfFilter && expandedBecauseOfCW) {
        onToggleHidden(status);
        this.handleFilterToggle();
      } else {
        this.handleFilterToggle();
      }
    } else {
      onToggleHidden(status);
    }
  };

  handleHotkeyToggleSensitive = () => {
    this.handleToggleMediaVisibility();
  };

  handleFilterToggle = () => {
    this.setState(state => ({ ...state, showDespiteFilter: !state.showDespiteFilter }));
  };

  _properStatus() {
    const { status } = this.props;
    if (!status || !status.get) {
      return null;
    }

    if (status.get('reblog', null) !== null && typeof status.get('reblog') === 'object') {
      return status.get('reblog');
    } else {
      return status;
    }
  }

  handleRef = c => {
    this.node = c;
  };

  isShowReplyInHome = () => {
    const path = window.location.pathname;
    return path.startsWith('/home');
  };

  isShowOnProfile = () => {
    const path = window.location.pathname;
    return /^\/@[^/]+$/.test(path);
  };

  render() {
    const { intl, hidden, featured, unfocusable, unread, showThread, scrollKey, pictureInPicture, previousId, nextInReplyToId, rootId, skipPrepend, avatarSize } = this.props;

    let { status, account, ...other } = this.props;

    if (status === null || (status.get('in_reply_to_account_id') !== null && this.isShowReplyInHome()) || (status.get('in_reply_to_account_id') !== null && this.isShowOnProfile())) {
      return null;
    }

    const properStatus = this._properStatus();

    if (!properStatus) {
      return null;
    }

    const handlers = this.props.muted ? {} : {
      reply: this.handleHotkeyReply,
      favourite: this.handleHotkeyFavourite,
      boost: this.handleHotkeyBoost,
      mention: this.handleHotkeyMention,
      open: this.handleHotkeyOpen,
      openProfile: this.handleHotkeyOpenProfile,
      moveUp: this.handleHotkeyMoveUp,
      moveDown: this.handleHotkeyMoveDown,
      toggleHidden: this.handleHotkeyToggleHidden,
      toggleSensitive: this.handleHotkeyToggleSensitive,
      openMedia: this.handleHotkeyOpenMedia,
      onTranslate: this.handleTranslate,
    };

    let media, statusAvatar, prepend, rebloggedByText;

    const connectUp = previousId && previousId === status.get('in_reply_to_id');
    const connectToRoot = rootId && rootId === status.get('in_reply_to_id');
    const connectReply = nextInReplyToId && nextInReplyToId === status.get('id');
    const matchedFilters = status.get('matched_filters');

    let isReblog = false;
    let reblogger = null;
    let reblogStatus = status;
    let caption = null;

    if (status.get('reblog', null) !== null && typeof status.get('reblog') === 'object') {
      isReblog = true;
      reblogger = status.get('account');
      reblogStatus = status.get('reblog');
      caption = status.get('reblog_caption') ? status.get('reblog_caption') : status.get('content');
      account = status.get('account');
      rebloggedByText = intl.formatMessage({ id: 'status.reblogged_by', defaultMessage: '{name} boosted' }, { name: status.getIn(['account', 'acct']) });
    }

    if (featured) {
      prepend = (
        <div className='status__prepend'>
          <div className='status__prepend__icon'><Icon id='thumb-tack' icon={PushPinIcon} /></div>
          <FormattedMessage id='status.pinned' defaultMessage='Pinned post' />
        </div>
      );
    } else if (status.get('visibility') === 'direct') {
      prepend = (
        <div className='status__prepend'>
          <div className='status__prepend__icon'><Icon id='at' icon={MentionIcon} /></div>
          <FormattedMessage id='status.direct_indicator' defaultMessage='Private mention' />
        </div>
      );
    } else if (showThread && status.get('in_reply_to_id')) {
      prepend = (
        <StatusThreadLabel accountId={status.getIn(['account', 'id'])} inReplyToAccountId={status.get('in_reply_to_account_id')} />
      );
    }

    const expanded = this.props.isComment ? true : (!matchedFilters || this.state.showDespiteFilter) && (!status.get('hidden') || status.get('spoiler_text').length === 0);

    if (hidden) {
      return (
        <HotKeys handlers={handlers} tabIndex={unfocusable ? null : -1}>
          <div ref={this.handleRef} className={classNames('status__wrapper', { focusable: !this.props.muted })} tabIndex={unfocusable ? null : 0}>
            <span>{status.getIn(['account', 'display_name']) || status.getIn(['account', 'username'])}</span>
            {status.get('spoiler_text').length > 0 && (<span>{status.get('spoiler_text')}</span>)}
            {expanded && <span>{status.get('content')}</span>}
          </div>
        </HotKeys>
      );
    }

    if (pictureInPicture.get('inUse')) {
      media = <PictureInPicturePlaceholder aspectRatio={this.getAttachmentAspectRatio()} />;
    } else if (this._properStatus().get('media_attachments').size > 0) {
      const language = status.getIn(['translation', 'language']) || status.get('language');
      const hasMultipleAttachments = this._properStatus().get('media_attachments').size > 1;
      const isSingleImageType = ['image', 'gifv', 'unknown'].includes(this._properStatus().getIn(['media_attachments', 0, 'type']));

      if (!this.props.isImageModal && isSingleImageType || !this.props.isImageModal && hasMultipleAttachments) {
        media = (
          <Bundle fetchComponent={MediaGallery} loading={this.renderLoadingMediaGallery}>
            {Component => (
              <Component
                media={this._properStatus().get('media_attachments')}
                lang={language}
                sensitive={this._properStatus().get('sensitive')}
                height={110}
                onOpenMedia={this.handleOpenMedia}
                cacheWidth={this.props.cacheMediaWidth}
                defaultWidth={this.props.cachedMediaWidth}
                visible={this.state.showMedia}
                onToggleVisibility={this.handleToggleMediaVisibility}
                statusId={status.get('id')}
                accountId={status.getIn(['account', 'id'])}
              />
            )}
          </Bundle>
        );
      } else if (this._properStatus().getIn(['media_attachments', 0, 'type']) === 'audio') {
        const attachment = this._properStatus().getIn(['media_attachments', 0]);
        const description = attachment.getIn(['translation', 'description']) || attachment.get('description');
        media = (
          <Bundle fetchComponent={Audio} loading={this.renderLoadingAudioPlayer}>
            {Component => (
              <Component
                src={attachment.get('url')}
                alt={description}
                lang={language}
                poster={attachment.get('preview_url') || this._properStatus().getIn(['account', 'avatar_static'])}
                backgroundColor={attachment.getIn(['meta', 'colors', 'background'])}
                foregroundColor={attachment.getIn(['meta', 'colors', 'foreground'])}
                accentColor={attachment.getIn(['meta', 'colors', 'accent'])}
                duration={attachment.getIn(['meta', 'original', 'duration'], 0)}
                width={this.props.cachedMediaWidth}
                height={110}
                cacheWidth={this.props.cacheMediaWidth}
                deployPictureInPicture={pictureInPicture.get('available') ? this.handleDeployPictureInPicture : undefined}
                sensitive={this._properStatus().get('sensitive')}
                blurhash={attachment.get('blurhash')}
                visible={this.state.showMedia}
                onToggleVisibility={this.handleToggleMediaVisibility}
              />
            )}
          </Bundle>
        );
      } else if (this._properStatus().getIn(['media_attachments', 0, 'type']) === 'video') {
        const attachment = this._properStatus().getIn(['media_attachments', 0]);
        const description = attachment.getIn(['translation', 'description']) || attachment.get('description');
        media = !this.props.isVideoModal && (
          <Bundle fetchComponent={Video} loading={this.renderLoadingVideoPlayer}>
            {Component => (
              <Component
                preview={attachment.get('preview_url')}
                frameRate={attachment.getIn(['meta', 'original', 'frame_rate'])}
                {...(!this.props.isComment && {
                  aspectRatio: `${attachment.getIn(['meta', 'original', 'width'])} / ${attachment.getIn(['meta', 'original', 'height'])}`,
                })}
                blurhash={attachment.get('blurhash')}
                src={attachment.get('url')}
                alt={description}
                lang={language}
                sensitive={this._properStatus().get('sensitive')}
                onOpenVideo={this.handleOpenVideo}
                deployPictureInPicture={pictureInPicture.get('available') ? this.handleDeployPictureInPicture : undefined}
                visible={this.state.showMedia}
                onToggleVisibility={this.handleToggleMediaVisibility}
                statusId={status.get('id')}
                accountId={status.getIn(['account', 'id'])}
              />
            )}
          </Bundle>
        );
      }
    } else if (this._properStatus().get('card')) {
      media = (
        <Card
          onOpenMedia={this.handleOpenMedia}
          card={this._properStatus().get('card')}
          compact
          sensitive={this._properStatus().get('sensitive')}
        />
      );
    }

    statusAvatar = <Avatar account={this._properStatus().get('account')} size={avatarSize ? avatarSize : 36} />;

    const { statusContentProps, hashtagBar } = getHashtagBarForStatus(this._properStatus());

    const hasHighlighted = this.state.isHighlighted && !this.state.isLikeComment;

    return (
      <HotKeys handlers={handlers} tabIndex={unfocusable ? null : -1}>
        <div className='background__wrapper_color'>
          <div
            className={classNames('status__wrapper', `status__wrapper-${status.get('visibility')}`, {
              'status__wrapper-reply': !!status.get('in_reply_to_id'),
              unread,
              focusable: !this.props.muted,
            })}
            tabIndex={this.props.muted || unfocusable ? null : 0}
            data-featured={featured ? 'true' : null}
            aria-label={textForScreenReader(intl, status, rebloggedByText)}
            ref={this.handleRef}
            data-nosnippet={status.getIn(['account', 'noindex'], true) || undefined}
            onMouseDown={this.handleMove}
          >
            {!skipPrepend && prepend}

            <div
              className={classNames('status', `status-${status.get('visibility')}`, {
                'status-reply': !!status.get('in_reply_to_id'),
                'status--in-thread': !!rootId,
                'status--first-in-thread': previousId && (!connectUp || connectToRoot),
                'status--highlighted': hasHighlighted,
                muted: this.props.muted,
              })}
              data-id={status.get('id')}
              tabIndex={0}
            >
              {(connectReply || connectUp || connectToRoot) && (
                <div
                  className={classNames('status__line', {
                    'status__line--full': connectReply,
                    'status__line--first': !status.get('in_reply_to_id') && !connectToRoot,
                  })}
                />
              )}

              {isReblog && (
                <Link
                  to={`/@${reblogger.get('acct')}/${status.get('id')}`}
                  title={reblogger.get('acct')}
                  data-hover-card-account={reblogger.get('id')}
                  className='status__reblogger-info'
                  style={{ textDecoration: 'none' }}
                >
                  <Link
                    to={`/@${reblogger.get('acct')}`}
                    title={reblogger.get('acct')}
                    data-hover-card-account={reblogger.get('id')}
                    className='status__display-name'
                  >
                    <div className='status__avatar'>
                      <Avatar account={reblogger} size={36} />
                    </div>
                    <div className='status__reblogger-details'>
                      <div className='status_account_and_time_and_follow_container'>
                        <DisplayName account={reblogger} />
                        {reblogger && reblogger.get('id') !== me && <FollowButton accountId={reblogger.get('id')} onlyFollow />}
                      </div>
                      <div className='status__reblogger-time'>
                        <RelativeTimestamp timestamp={status.get('created_at')} />
                        <span className='dot-separator-tiny'>•</span>
                        <span className='status__visibility-icon'>
                          <VisibilityIcon visibility={status.get('visibility')} />
                        </span>
                        {status.get('edited_at') && (
                          <abbr
                            title={intl.formatMessage(messages.edited, {
                              date: intl.formatDate(status.get('edited_at'), {
                                year: 'numeric',
                                month: 'short',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              }),
                            })}
                          />
                        )}
                      </div>
                    </div>
                  </Link>

                  <div className='status__caption'>
                    <span dangerouslySetInnerHTML={{ __html: caption }} />
                  </div>

                </Link>
              )}

              <div
                className={classNames('status__content-wrapper', {
                  'status__content-wrapper--reblog': isReblog,
                })}
              >
                <div onMouseUp={this.handleMouseUp} className='status__info'>
                  <div className='status__detail-button-wrapper' />
                  <Link
                    to={`/@${reblogStatus.getIn(['account', 'acct'])}`}
                    title={reblogStatus.getIn(['account', 'acct'])}
                    data-hover-card-account={reblogStatus.getIn(['account', 'id'])}
                    className='status__display-name'
                  >
                    <div className='status__avatar'>{statusAvatar}</div>
                    <div className='status_account_and_time_container' >
                      <DisplayName account={reblogStatus.get('account')} />
                      <div role='presentation' className='status__relative-time' onClick={e => e.preventDefault()}>
                        <RelativeTimestamp timestamp={reblogStatus.get('created_at')} />
                        <span className='dot-separator-tiny'>•</span>
                        <span className='status__visibility-icon'>
                          <VisibilityIcon visibility={reblogStatus.get('visibility')} />
                        </span>
                        {reblogStatus.get('edited_at') && (
                          <abbr
                            title={intl.formatMessage(messages.edited, {
                              date: intl.formatDate(reblogStatus.get('edited_at'), {
                                year: 'numeric',
                                month: 'short',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              }),
                            })}
                          />
                        )}
                      </div>
                    </div>
                  </Link>
                  {reblogStatus && reblogStatus.getIn(['account', 'id']) !== me && <FollowButton accountId={reblogStatus.getIn(['account', 'id'])} onlyFollow />}
                </div>

                {!this.props.isComment && matchedFilters && (
                  <FilterWarning
                    title={matchedFilters.join(', ')}
                    expanded={this.state.showDespiteFilter}
                    onClick={this.handleFilterToggle}
                  />
                )}
                {!this.props.isComment && status.get('spoiler_text').length > 0 && (!matchedFilters || this.state.showDespiteFilter) && (
                  <ContentWarning
                    text={status.getIn(['translation', 'spoilerHtml']) || status.get('spoilerHtml')}
                    expanded={expanded}
                    onClick={this.handleExpandedToggle}
                  />
                )}
                {(this.props.isComment || expanded) && (
                  <>
                    {this._properStatus().get('sensitive') && !this.state.showSensitiveContent && !status.get('in_reply_to_id') ? (
                      <div className='status__sensitive-warning'>
                        <button
                          className='status__sensitive-link'
                          onClick={this.handleToggleSensitiveContent}
                        >
                          <FormattedMessage id='status.sensitive_preview' defaultMessage='this-status-is-hidden' />
                        </button>
                      </div>
                    ) : (
                      <>
                        <StatusContent
                          status={this._properStatus()}
                          onClick={this.handleClick}
                          onTranslate={this.handleTranslate}
                          collapsible
                          onCollapsedToggle={this.handleCollapsedToggle}
                          {...statusContentProps}
                          isComment={this.props.isComment}
                          contextType={this.props.contextType}
                          isImageModal={this.props.isImageModal}
                        />
                        {hashtagBar}
                        {media}
                      </>
                    )}
                  </>
                )}
              </div>
              <StatusActionBar
                replyLevel={this.props.replyLevel}
                scrollKey={scrollKey}
                status={status}
                account={account}
                onToggleReplies={this.props.onToggleReplies}
                onShowCommentForm={this.props.onShowCommentForm}
                {...other}
                isComment={this.props.isComment}
                featured={featured}
                isImageModal={this.props.isImageModal}
                isVideoModal={this.props.isVideoModal}
                isReplyExpand={this.props.isReplyExpand}
                expandReply={this.props.expandReply}
                childRepliesSize={this.props.childRepliesSize}
                onReply={this.props.onReply}
                isNotification={this.props.isNotification}
              />
            </div>
          </div>
        </div>
      </HotKeys>
    );
  }
}

export default withOptionalRouter(injectIntl(Status));
